/**
 * PATH: apps/ai/src/pages/dashboard/MessageBubble.tsx
 * PURPOSE: Chat message bubble with markdown rendering and feedback buttons
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Sparkles, Copy, Check, Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { Message } from './types';
import { getAccessToken, getTenantId } from '../../services/streaming/auth';
import { API_BASE } from '@quotech/api';

function isFileDownloadLink(href: string | undefined): boolean {
  return !!href && (
    href.includes('/api/qdrive/files/') || href.includes('/api/files/')
  ) && href.includes('/download');
}

async function handleFileDownload(url: string) {
  const token = getAccessToken();
  if (!token) { window.open(url, '_blank'); return; }

  const tenantId = getTenantId();
  const apiUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  try {
    const res = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Organisation-ID': tenantId,
      },
    });
    if (!res.ok) {
      alert('Download failed — file may not exist or access is denied.');
      return;
    }
    const data = await res.json();
    if (data.url) {
      window.open(data.url, '_blank');
    } else {
      alert('Original file is not available for download. Try re-processing via Context Builder.');
    }
  } catch {
    alert('Download failed — network error.');
  }
}

interface MessageBubbleProps {
  message: Message;
  onCopy: (content: string, id: string) => void;
  copied: string | null;
  onFeedback?: (messageId: string, rating: 1 | -1) => void;
  feedbackSubmitting?: string | null;
}

export function MessageBubble({ message, onCopy, copied, onFeedback, feedbackSubmitting }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const hasFeedback = message.feedbackRating != null;
  const isSubmitting = feedbackSubmitting === message.id;

  return (
    <div className={cn("flex gap-3 group animate-in fade-in slide-in-from-bottom-2 duration-300", isUser ? "flex-row-reverse" : "flex-row")}>
      <div className={cn(
        "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md mt-1",
        isUser ? "bg-foreground" : "bg-gradient-to-br from-primary to-primary/80 shadow-primary/20",
      )}>
        {isUser ? <User className="w-4 h-4 text-background" /> : <Sparkles className="w-4 h-4 text-primary-foreground" />}
      </div>

      <div className={cn("flex-1 min-w-0 max-w-[90%]", isUser ? "text-right" : "text-left")}>
        <div className={cn(
          "inline-block px-4 py-3 rounded-2xl text-sm leading-relaxed",
          isUser ? "bg-foreground text-background rounded-br-md" : "bg-card text-foreground rounded-bl-md border border-border shadow-sm",
        )}>
          {isUser ? (
            <div className="whitespace-pre-wrap break-words">{message.content || "..."}</div>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none
              prose-headings:font-semibold prose-headings:text-foreground prose-headings:mt-4 prose-headings:mb-2
              prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-h4:text-xs
              prose-p:my-2 prose-p:text-foreground/90
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-2 prose-ul:pl-4 prose-ol:my-2 prose-ol:pl-4
              prose-li:my-0.5 prose-li:text-foreground/90
              prose-code:bg-accent prose-code:text-primary prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-['']
              prose-pre:bg-accent/80 prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:p-3 prose-pre:my-3 prose-pre:overflow-x-auto
              prose-table:my-3 prose-table:w-full prose-table:border-collapse
              prose-th:bg-accent prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-th:text-xs prose-th:text-foreground
              prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 prose-td:text-xs
              prose-tr:even:bg-accent/30
              prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-primary/80
              prose-blockquote:border-l-4 prose-blockquote:border-primary/50 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
              prose-hr:border-border prose-hr:my-4
            ">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children, ...props }) => {
                    if (isFileDownloadLink(href)) {
                      return (
                        <a
                          href="#"
                          onClick={async (e: React.MouseEvent) => { e.preventDefault(); await handleFileDownload(href!); }}
                          className="text-primary underline underline-offset-2 cursor-pointer hover:text-primary/80"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    }
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                      </a>
                    );
                  },
                }}
              >{message.content || "..."}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Actions + Feedback */}
        {!isUser && message.content && (
          <div className="mt-1.5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onCopy(message.content, message.id)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors">
              {copied === message.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>

            {onFeedback && (
              <div className="flex items-center gap-1 border-l border-border pl-2">
                <button onClick={() => onFeedback(message.id, 1)} disabled={isSubmitting || hasFeedback}
                  className={cn("p-1.5 rounded-md transition-all duration-200",
                    message.feedbackRating === 1 ? "bg-green-500/20 text-green-500" : "text-muted-foreground hover:text-green-500 hover:bg-green-500/10",
                    (isSubmitting || hasFeedback) && message.feedbackRating !== 1 && "opacity-50 cursor-not-allowed",
                  )} title="Helpful">
                  {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className={cn("w-3 h-3", message.feedbackRating === 1 && "fill-current")} />}
                </button>
                <button onClick={() => onFeedback(message.id, -1)} disabled={isSubmitting || hasFeedback}
                  className={cn("p-1.5 rounded-md transition-all duration-200",
                    message.feedbackRating === -1 ? "bg-red-500/20 text-red-500" : "text-muted-foreground hover:text-red-500 hover:bg-red-500/10",
                    (isSubmitting || hasFeedback) && message.feedbackRating !== -1 && "opacity-50 cursor-not-allowed",
                  )} title="Not helpful">
                  {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsDown className={cn("w-3 h-3", message.feedbackRating === -1 && "fill-current")} />}
                </button>
                {hasFeedback && <span className="text-[10px] text-muted-foreground ml-1">Thanks!</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
